import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';

export interface DateFilterContentProps {
  title: string;
  selectedYears: string[];
  selectedMonths: string[];
  onYearSelectionChange: (years: string[]) => void;
  onMonthSelectionChange: (months: string[]) => void;
  availableYears: string[];
  availableMonths: string[];
  onClose: () => void;
}

export const DateFilterContent: React.FC<DateFilterContentProps> = ({
  title,
  selectedYears,
  selectedMonths,
  onYearSelectionChange,
  onMonthSelectionChange,
  availableYears,
  availableMonths,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<'years' | 'months'>('years');

  const handleYearToggle = (year: string) => {
    const newSelection = selectedYears.includes(year)
      ? selectedYears.filter(y => y !== year)
      : [...selectedYears, year];
    onYearSelectionChange(newSelection);
  };

  const handleMonthToggle = (month: string) => {
    const newSelection = selectedMonths.includes(month)
      ? selectedMonths.filter(m => m !== month)
      : [...selectedMonths, month];
    onMonthSelectionChange(newSelection);
  };

  const handleSelectAllYears = () => {
    onYearSelectionChange(availableYears);
  };

  const handleClearAllYears = () => {
    onYearSelectionChange([]);
  };

  const handleSelectAllMonths = () => {
    onMonthSelectionChange(availableMonths);
  };

  const handleClearAllMonths = () => {
    onMonthSelectionChange([]);
  };

  const handleClearAll = () => {
    onYearSelectionChange([]);
    onMonthSelectionChange([]);
  };

  const totalActiveFilters = selectedYears.length + selectedMonths.length;
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="py-1">
      {/* Header */}
      <div className="px-4 py-2 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900">Filter by {title}</h3>
          {totalActiveFilters > 0 && (
            <button
              onClick={handleClearAll}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center"
            >
              <XMarkIcon className="h-3 w-3 mr-1" />
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 py-2 border-b border-gray-200">
        <div className="flex space-x-1 bg-gray-100 rounded-md p-1">
          <button
            onClick={() => setActiveTab('years')}
            className={cn(
              'flex-1 py-1 px-3 text-sm font-medium rounded-md transition-colors',
              activeTab === 'years'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            Years ({selectedYears.length})
          </button>
          <button
            onClick={() => setActiveTab('months')}
            className={cn(
              'flex-1 py-1 px-3 text-sm font-medium rounded-md transition-colors',
              activeTab === 'months'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            Months ({selectedMonths.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-h-64 overflow-y-auto">
        {activeTab === 'years' && (
          <div className="px-4 py-2">
            {/* Year controls */}
            <div className="flex justify-between text-xs mb-3">
              <button
                onClick={handleSelectAllYears}
                className="text-indigo-600 hover:text-indigo-800"
              >
                Select All
              </button>
              <button
                onClick={handleClearAllYears}
                className="text-gray-500 hover:text-gray-700"
              >
                Clear All
              </button>
            </div>

            {/* Years grid */}
            <div className="grid grid-cols-3 gap-2">
              {availableYears.map(year => (
                <label
                  key={year}
                  className="flex items-center py-1 px-2 hover:bg-gray-100 rounded-md cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedYears.includes(year)}
                    onChange={() => handleYearToggle(year)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-2"
                  />
                  <span className="text-sm text-gray-900">{year}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'months' && (
          <div className="px-4 py-2">
            {/* Month controls */}
            <div className="flex justify-between text-xs mb-3">
              <button
                onClick={handleSelectAllMonths}
                className="text-indigo-600 hover:text-indigo-800"
              >
                Select All
              </button>
              <button
                onClick={handleClearAllMonths}
                className="text-gray-500 hover:text-gray-700"
              >
                Clear All
              </button>
            </div>

            {/* Months grid */}
            <div className="grid grid-cols-2 gap-2">
              {monthNames.map(month => (
                <label
                  key={month}
                  className="flex items-center py-1 px-2 hover:bg-gray-100 rounded-md cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedMonths.includes(month)}
                    onChange={() => handleMonthToggle(month)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-2"
                  />
                  <span className="text-sm text-gray-900">{month}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

